import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  Select,
  Spinner,
  Switch,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import ProfileBanner from '../components/profileScreen/ProfileBanner';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { getUserByFirebaseUid } from 'api/api';
import API from 'api/api';
import { useTranslation } from 'i18n/TranslationProvider';
import { LANGUAGES } from 'i18n/languages';
import LanguageSelector from 'components/i18n/LanguageSelector';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import { UnifiedSurfaceCard } from 'components/layout/UnifiedPrimitives';
import { deleteAiSession } from 'domains/ai/services/aiService';
import { AI_UI_EVENT_TYPES, trackAiEvent } from 'domains/ai/services/aiAnalytics';
import { isAiAssistantEnabledForRole } from 'domains/ai/utils/availability';
import {
  clearAiSessionId,
  getAiPreferences,
  getAiSessionId,
  setAiPreferences,
} from 'domains/ai/utils/storage';

const mapUserToForm = (user) => ({
  displayName: user?.displayName || '',
  email: user?.email || '',
  phoneNumber: user?.phoneNumber || '',
  companyName: user?.companyName || '',
  companyAddress: user?.companyAddress || '',
  companyCity: user?.companyCity || '',
  companyPostCode: user?.companyPostCode || '',
  companyCountry: user?.companyCountry || '',
  vatNumber: user?.vatNumber || '',
});

const UserProfileScreen = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { uid, loading: authLoading } = useFirebaseAuth();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState(mapUserToForm(null));
  const [initialFormData, setInitialFormData] = useState(mapUserToForm(null));
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [aiPreferences, setAiPreferencesState] = useState(() => getAiPreferences());
  const [clearingAiHistory, setClearingAiHistory] = useState(false);
  const [platformTranslationEnabled, setPlatformTranslationEnabled] = useState(false);
  const [userTranslationEnabled, setUserTranslationEnabled] = useState(false);
  const [userPreferredLanguage, setUserPreferredLanguage] = useState('en');

  // Check if user is a partner
  const isPartner = user?.role === 'partner';

  const validateForm = () => {
    const newErrors = {};
    
    // VAT is required only for Partners
    if (isPartner) {
      if (!formData.vatNumber || formData.vatNumber.trim() === '') {
        newErrors.vatNumber = t('VAT number is required');
      } else if (formData.vatNumber.length > 30) {
        newErrors.vatNumber = t('VAT number must not exceed 30 characters');
      }
    }

    // Company Name is required only for Partners
    if (isPartner) {
      if (!formData.companyName || formData.companyName.trim() === '') {
        newErrors.companyName = t('Company name is required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchUser = useCallback(async () => {
    if (authLoading) return;
    if (!uid) {
      setLoading(false);
      setError(t('Not logged in'));
      return;
    }
    setLoading(true);
    try {
      const profileUser = await getUserByFirebaseUid(uid);
      const mapped = mapUserToForm(profileUser);
      setUser(profileUser);
      setFormData(mapped);
      setInitialFormData(mapped);
      setError(null);
    } catch (e) {
      console.error('Error fetching user profile:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid, authLoading, t]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    API.get('/settings/public')
      .then((res) => {
        setPlatformTranslationEnabled(res.data?.liveTranslationEnabled === true);
        setUserTranslationEnabled(res.data?.userLiveTranslationEnabled === true);
        setUserPreferredLanguage(res.data?.userPreferredLanguage || 'en');
      })
      .catch(() => {});
  }, []);

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);
  const userRole = user?.role;
  const showAiSettings = userRole === 'client' || userRole === 'partner';
  const aiAvailableForRole = isAiAssistantEnabledForRole(userRole);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const handleSave = async () => {
    if (!uid || isSaving) return;
    
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);

    try {
      const payload = {
        displayName: formData.displayName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        companyName: formData.companyName.trim(),
        companyAddress: formData.companyAddress.trim(),
        companyCity: formData.companyCity.trim(),
        companyPostCode: formData.companyPostCode.trim(),
        companyCountry: formData.companyCountry.trim(),
        vatNumber: formData.vatNumber.trim(),
      };

      const response = await API.put(`/users/${uid}`, payload);
      const updatedUser = response.data;
      const mapped = mapUserToForm(updatedUser);

      setUser(updatedUser);
      setFormData(mapped);
      setInitialFormData(mapped);

      toast({
        title: t('Status updated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (saveError) {
      console.error('Error saving profile:', saveError);
      toast({
        title: t('Update failed'),
        description: saveError?.response?.data?.message || saveError.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAiPreferenceChange = (key) => (event) => {
    const nextValue = event.target.checked;
    const nextPreferences = setAiPreferences({
      [key]: nextValue,
    });
    setAiPreferencesState(nextPreferences);

    toast({
      title: t('AI assistant preferences updated'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClearAiHistory = useCallback(async () => {
    setClearingAiHistory(true);

    try {
      const rolesToClear = ['client', 'partner'];
      await Promise.all(
        rolesToClear.map(async (role) => {
          const sessionId = getAiSessionId(role);
          if (!sessionId) {
            clearAiSessionId(role);
            return;
          }

          try {
            await deleteAiSession(sessionId);
          } catch (clearError) {
            if (clearError?.response?.status !== 404) {
              throw clearError;
            }
          } finally {
            clearAiSessionId(role);
          }
        }),
      );

      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.SESSION_CLEARED,
        role: userRole,
        routeKey: 'profile',
      });

      toast({
        title: t('Assistant history cleared'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (clearError) {
      toast({
        title: t('Failed to clear assistant history'),
        description: clearError?.response?.data?.message || clearError?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setClearingAiHistory(false);
    }
  }, [t, toast, userRole]);

  const handleTranslationToggle = async (event) => {
    const enabled = event.target.checked;
    setUserTranslationEnabled(enabled);
    try {
      await API.patch('/users/me/translation-preferences', { liveTranslationEnabled: enabled });
      toast({ title: t('Translation preference updated'), status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      setUserTranslationEnabled(!enabled);
      toast({ title: t('Failed to update translation preference'), status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleTranslationLanguageChange = async (event) => {
    const lang = event.target.value;
    setUserPreferredLanguage(lang);
    try {
      await API.patch('/users/me/translation-preferences', { preferredLanguage: lang });
      toast({ title: t('Preferred language updated'), status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: t('Failed to update preferred language'), status: 'error', duration: 3000, isClosable: true });
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Box textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('Loading profile...')}</Text>
        </Box>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
      <Box textAlign="center" color="red.500">
        <Text>
          {t('Error loading profile')}: {error}
        </Text>
      </Box>
      </PageShell>
    );
  }

  if (!user) {
    return (
      <PageShell>
      <Box textAlign="center">
        <Text>{t('User not found.')}</Text>
      </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
      <PageHeader
        title={t('Profile')}
        description={t('Manage the company and contact details that appear across your listings, invoices, and account verification flows.')}
        actions={
          <HStack w={{ base: '100%', md: 'auto' }} justify={{ base: 'stretch', md: 'flex-end' }}>
            <Button
              variant="outline"
              borderRadius="14px"
              onClick={handleReset}
              isDisabled={!isDirty || isSaving}
              w={{ base: '100%', md: 'auto' }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="brand"
              borderRadius="14px"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={!isDirty || isSaving}
              w={{ base: '100%', md: 'auto' }}
            >
              {t('Save')}
            </Button>
          </HStack>
        }
      />
      <ProfileBanner user={user} />

      <UnifiedSurfaceCard p={{ base: 4, md: 6 }}>
        <VStack spacing={5} align="stretch">
          <HStack justify="space-between" flexWrap="wrap">
            <Text fontWeight="bold" fontSize="xl">
              {t('Company Information')}
            </Text>
          </HStack>

          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
            gap={4}
          >
            <FormControl>
              <FormLabel>{t('Name')}</FormLabel>
              <Input
                value={formData.displayName}
                onChange={handleInputChange('displayName')}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('Email')}</FormLabel>
              <Input value={formData.email} isReadOnly isDisabled />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {t('Email cannot be changed.')}
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('Phone Number')}</FormLabel>
              <Input
                value={formData.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
              />
            </FormControl>

            <FormControl isRequired={isPartner} isInvalid={Boolean(errors.companyName)}>
              <FormLabel>{t('Company Name')}</FormLabel>
              <Input
                value={formData.companyName}
                onChange={handleInputChange('companyName')}
              />
              <FormErrorMessage>{errors.companyName}</FormErrorMessage>
            </FormControl>

            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl>
                <FormLabel>{t('Address')}</FormLabel>
                <Input
                  value={formData.companyAddress}
                  onChange={handleInputChange('companyAddress')}
                />
              </FormControl>
            </GridItem>

            <FormControl>
              <FormLabel>{t('City')}</FormLabel>
              <Input
                value={formData.companyCity}
                onChange={handleInputChange('companyCity')}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('Post code')}</FormLabel>
              <Input
                value={formData.companyPostCode}
                onChange={handleInputChange('companyPostCode')}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('Country')}</FormLabel>
              <Input
                value={formData.companyCountry}
                onChange={handleInputChange('companyCountry')}
              />
            </FormControl>

            <FormControl isInvalid={Boolean(errors.vatNumber)} isRequired={isPartner}>
              <FormLabel>{t('VAT Number')}</FormLabel>
              <Input
                value={formData.vatNumber}
                onChange={handleInputChange('vatNumber')}
                placeholder="e.g. BG123456789"
                maxLength={30}
              />
              <FormErrorMessage>{errors.vatNumber}</FormErrorMessage>
            </FormControl>
          </Grid>

          <HStack justify="space-between" pt={2} flexWrap="wrap" gap={3}>
            <Text fontSize="sm" color="gray.500">
              {t('Keep these details up to date so listings, invoices, and partner verification stay accurate.')}
            </Text>
            <Text fontSize="sm" fontWeight="700" color={isDirty ? 'brand.500' : 'gray.500'}>
              {isDirty ? t('Unsaved changes') : t('All changes saved')}
            </Text>
          </HStack>
        </VStack>
      </UnifiedSurfaceCard>

      {showAiSettings ? (
      <UnifiedSurfaceCard p={{ base: 4, md: 6 }}>
        <VStack spacing={5} align="stretch">
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <Text fontWeight="bold" fontSize="xl">
                {t('AI Assistant')}
              </Text>
              <Text mt={1} fontSize="sm" color="gray.500">
                {t('Control how the web assistant behaves on this device. These settings do not change your account data.')}
              </Text>
            </Box>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              onClick={handleClearAiHistory}
              isLoading={clearingAiHistory}
            >
              {t('Clear assistant history')}
            </Button>
          </HStack>

          {!aiAvailableForRole ? (
            <Text fontSize="sm" color="gray.500">
              {t('The AI assistant is currently disabled for this environment. Existing local session data can still be cleared here.')}
            </Text>
          ) : null}

          <VStack spacing={4} align="stretch" divider={<Divider />}>
            <HStack justify="space-between" align="flex-start" spacing={4}>
              <Box flex="1">
                <Text fontWeight="700" color="navy.700">
                  {t('Show AI launchers')}
                </Text>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {t('Show or hide AI assistant buttons on supported dashboard and listing pages.')}
                </Text>
              </Box>
              <Switch
                colorScheme="blue"
                isChecked={aiPreferences.showLaunchers}
                onChange={handleAiPreferenceChange('showLaunchers')}
              />
            </HStack>

            <HStack justify="space-between" align="flex-start" spacing={4}>
              <Box flex="1">
                <Text fontWeight="700" color="navy.700">
                  {t('Remember assistant conversation')}
                </Text>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {t('Keep the current AI session available after closing the browser on this device.')}
                </Text>
              </Box>
              <Switch
                colorScheme="blue"
                isChecked={aiPreferences.rememberSession}
                onChange={handleAiPreferenceChange('rememberSession')}
              />
            </HStack>

            <HStack justify="space-between" align="flex-start" spacing={4}>
              <Box flex="1">
                <Text fontWeight="700" color="navy.700">
                  {t('Open prepared screens automatically')}
                </Text>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {t('After you confirm an AI action, open the prepared destination immediately instead of waiting for a manual click.')}
                </Text>
              </Box>
              <Switch
                colorScheme="blue"
                isChecked={aiPreferences.autoOpenPreparedScreens}
                onChange={handleAiPreferenceChange('autoOpenPreparedScreens')}
              />
            </HStack>
          </VStack>
        </VStack>
      </UnifiedSurfaceCard>
      ) : null}

      <UnifiedSurfaceCard p={{ base: 4, md: 6 }}>
        <VStack spacing={5} align="stretch">
          <Box>
            <Text fontWeight="bold" fontSize="xl">
              {t('Language & Region')}
            </Text>
            <Text mt={1} fontSize="sm" color="gray.500">
              {t('Choose the interface language. This preference is saved locally and applies immediately.')}
            </Text>
          </Box>
          <HStack justify="space-between" align="center" spacing={4}>
            <Text fontWeight="700" color="navy.700">
              {t('Interface language')}
            </Text>
            <LanguageSelector showLabel />
          </HStack>
        </VStack>
      </UnifiedSurfaceCard>

      {platformTranslationEnabled ? (
      <UnifiedSurfaceCard p={{ base: 4, md: 6 }}>
        <VStack spacing={5} align="stretch">
          <Box>
            <Text fontWeight="bold" fontSize="xl">
              {t('Live Translation')}
            </Text>
            <Text mt={1} fontSize="sm" color="gray.500">
              {t('Automatically translate messages from other users into your preferred language using AI.')}
            </Text>
          </Box>

          <VStack spacing={4} align="stretch" divider={<Divider />}>
            <HStack justify="space-between" align="flex-start" spacing={4}>
              <Box flex="1">
                <Text fontWeight="700" color="navy.700">
                  {t('Enable live translation')}
                </Text>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {t('When enabled, messages from other users will be automatically translated to your preferred language.')}
                </Text>
              </Box>
              <Switch
                colorScheme="blue"
                isChecked={userTranslationEnabled}
                onChange={handleTranslationToggle}
              />
            </HStack>

            <HStack justify="space-between" align="flex-start" spacing={4}>
              <Box flex="1">
                <Text fontWeight="700" color="navy.700">
                  {t('Preferred language')}
                </Text>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {t('Messages will be translated into this language.')}
                </Text>
              </Box>
              <Select
                size="sm"
                maxW="200px"
                borderRadius="lg"
                value={userPreferredLanguage}
                onChange={handleTranslationLanguageChange}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </Select>
            </HStack>
          </VStack>
        </VStack>
      </UnifiedSurfaceCard>
      ) : null}
      </VStack>
    </PageShell>
  );
};

export default UserProfileScreen;
